'use client';

import { useEffect } from 'react';

// Matched with closest() rather than against the target's own tag, so a keystroke inside a rich-text
// region counts as typing even when it lands on a child element. The attribute is read instead of
// `isContentEditable` because jsdom doesn't implement that property.
const TYPING_SELECTOR = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

/** Whether a keystroke landed in something the player is typing into, and so isn't ours to take. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as Element | null;
  return typeof el?.closest === 'function' && el.closest(TYPING_SELECTOR) !== null;
}

/**
 * Ctrl-Z — Cmd-Z on a Mac — undoes the last move.
 *
 * Listens on the window rather than the board: no game gives the grid focus, and a player who last
 * clicked Hint still expects the shortcut to work. Two chords are deliberately left alone — anything
 * typed into a field, and Shift-Cmd-Z, which means redo everywhere else and we have no redo to give.
 *
 * `enabled` should track whatever gates the Undo button itself, so the keyboard can never reach a
 * move the button wouldn't.
 */
export function useUndoShortcut(handleUndo: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'z' && e.key !== 'Z') return;
      // Either modifier, so a Mac user on an external PC keyboard gets it too. Alt-Cmd-Z isn't ours.
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      handleUndo();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleUndo, enabled]);
}
